// Access (AdminPerson.dc.html): what <name> can open, and why, 1 row per access row, plus a 1-2-1
// client's space and ownership outside the hub, labelled (data-model 4.9). Owner and admin get Revoke
// (revoke-access: progress kept; their sessions end only when nothing else of theirs is open and the
// login isn't shared), Grant again, and a Change menu for what BUILD-PLAN stage 5 asks and no board
// draws: pause until a date, resume, a new end date, change cohort, move the balance date, 7 more days
// and the card link. Each change is a direct write under row level security (data-model 10.4), which
// stamps who did it and audits it. changeItems, ChangeForm and the 2 writes made outside a form (7 more days and the
// card link) are exported, so Masterminds' status line (the critique's admin 5) makes the person page's own writes.
// On a phone (the critique's admin 17) each row is 1 line, its product and status, that opens on tap, and Revoke
// access is the last item of its Change menu, still with its confirm.
import { useEffect, useRef, useState } from 'react'
import {
  Button, ButtonLink, Card, ConfirmPanel, DateField, Dot, Select, StatusPill, Table, TextInput, useAdmin,
  todayInUk, addDays, ukEndOfDay, ukToIso, ukDayOf,
} from '../ui/index.js'
import { usePhone } from '../../lib/media.js'
import { CHANGE, PERSON, SPACE, byBoardOrder, accessDetail, accessRuns, accessStatus, boardDay, homeSite, howGot, isCurrent, shortName, hhmm } from './words.js'
import { cohortsOf, grantable } from './data.js'
import { spacePath } from '../../products/mentorship/model.js'
import { liveSpaceProducts } from './OpenSpaceDialog.jsx'
import { standingFor, standingDetail } from '../mastermind/words.js'

const TONE = { 'Place secured': 'attention', Refunded: 'ended' }
const OUTSIDE_STATUS = { paid_in_full: 'Paid in full', secured: 'Place secured', active: 'Active', overdue: 'Overdue', paused: 'Paused', ended: 'Ended' }
const DAY = 24 * 3600 * 1000

// Whether a card link can go for a row: a subscription, and a Stripe customer on the row or the person
export const canSendCard = (billing, hasCustomer) => Boolean(billing?.stripe_subscription_id && (billing?.stripe_customer_id || hasCustomer))

// The Change menu's items for 1 row
export function changeItems(row, { cohorts, billing, hasCustomer, product }) {
  const items = []
  if (row.status === 'active' || row.status === 'overdue') items.push(['pause', CHANGE.pause])
  if (row.status === 'paused' && row.paused_by === 'staff') items.push(['resume', CHANGE.resume])
  if (isCurrent(row)) items.push(['ends', CHANGE.ends])
  if (isCurrent(row) && cohortsOf(cohorts, row.product_id).length) items.push(['cohort', product?.type === 'mastermind' ? CHANGE.group : CHANGE.cohort])
  if (row.status === 'secured') items.push(['balance', CHANGE.balance])
  if (row.status === 'overdue' && row.suspend_on) items.push(['grace', CHANGE.grace])
  if ((row.status === 'overdue' || row.status === 'suspended') && canSendCard(billing, hasCustomer)) items.push(['card', CHANGE.card])
  return items
}

// 7 more days on a failed payment: the day it's suspended moves on a week. The new day, as written.
export async function giveSevenDays(api, row) {
  const next = new Date(Date.parse(row.suspend_on) + 7 * DAY).toISOString()
  await api.update_access(row.id, { suspend_on: next })
  return next
}
// The card link (send-card-link): its done line comes back as the answer's message
export const sendCardLink = (api, { personId, row }) => api.send_card_link({ person_id: personId, access_id: row.id })

export function ChangeMenu({ label, items, onPick }) {
  const [open, setOpen] = useState(false)
  const box = useRef(null)
  const button = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { setOpen(false); button.current?.focus() } }
    const onDown = e => { if (box.current && !box.current.contains(e.target)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open])
  if (!items.length) return null
  return (
    <span className="apl-menu" ref={box}>
      <button ref={button} type="button" className="ad-btn ad-btn--line ad-btn--sm" aria-haspopup="true" aria-expanded={open}
        aria-label={label} onClick={() => setOpen(o => !o)}>{PERSON.change}</button>
      {open && (
        <span className="apl-menu-list" role="menu" aria-label={label}>
          {items.map(([key, text]) => (
            <button key={key} type="button" role="menuitem" className="apl-menu-item" onClick={() => { setOpen(false); onPick(key) }}>{text}</button>
          ))}
        </span>
      )}
    </span>
  )
}

// The form a Change item opens under its row (and under Masterminds' status line)
export function ChangeForm({ kind, row, product, cohorts, first, onDone, onCancel }) {
  const { api } = useAdmin()
  const name = product?.name || 'it'
  const [day, setDay] = useState(() => {
    if (kind === 'ends' && row.ends_at) return ukDayOf(row.ends_at)
    if (kind === 'balance' && row.balance_due_at) return ukDayOf(row.balance_due_at)
    return ''
  })
  const [reason, setReason] = useState('')
  const [cohortId, setCohortId] = useState(row.cohort_id || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const tomorrow = addDays(todayInUk(), 1)

  async function save(patch, message) {
    setBusy(true)
    setError(null)
    try {
      await api.update_access(row.id, patch)
      onDone(message)
    } catch (e) {
      setError(e)
      setBusy(false)
    }
  }

  const actions = (go, label, disabled) => (
    <span className="ad-confirm-actions">
      <Button kind="ink" size="sm" busy={busy} disabled={disabled} onClick={go}>{label}</Button>
      <Button size="sm" onClick={onCancel} disabled={busy}>{PERSON.keep}</Button>
    </span>
  )
  const err = error && <p className="ad-error" role="alert">{error.message}</p>

  if (kind === 'pause') {
    return (
      <div className="apl-change" role="group" aria-label={CHANGE.pause}>
        <p className="apl-change-line">{CHANGE.pauseLine(first, name)}</p>
        <div className="apl-change-fields">
          <DateField label={CHANGE.pauseUntil} value={day} onChange={setDay} min={tomorrow} plain />
          <label className="ad-field">
            <span className="ad-label ad-label--plain">{CHANGE.pauseReason}</span>
            <TextInput value={reason} onChange={setReason} placeholder={CHANGE.pauseReasonPlaceholder} maxLength={500} />
          </label>
        </div>
        {err}
        {actions(() => save({ status: 'paused', paused_until: day, pause_reason: reason.trim() || null }, CHANGE.done.pause(name, boardDay(day))), CHANGE.pauseGo, !day)}
      </div>
    )
  }
  if (kind === 'resume') {
    const to = row.payment_failed_at ? 'overdue' : !row.first_opened_at && row.balance_due_at ? 'secured' : 'active'
    return (
      <div className="apl-change" role="group" aria-label={CHANGE.resume}>
        <p className="apl-change-line">{CHANGE.resumeLine(first, name)}</p>
        {err}
        {actions(() => save({ status: to, paused_until: null, pause_reason: null }, CHANGE.done.resume(name)), CHANGE.resumeGo, false)}
      </div>
    )
  }
  if (kind === 'ends') {
    return (
      <div className="apl-change" role="group" aria-label={CHANGE.ends}>
        <p className="apl-change-line">{CHANGE.endsLine(first, name)}</p>
        <div className="apl-change-fields">
          <DateField label={CHANGE.endsLabel} value={day} onChange={setDay} min={tomorrow} plain />
        </div>
        {err}
        <span className="ad-confirm-actions">
          <Button kind="ink" size="sm" busy={busy} disabled={!day} onClick={() => save({ ends_at: ukEndOfDay(day) }, CHANGE.done.ends(name, boardDay(day)))}>{CHANGE.endsGo}</Button>
          {row.ends_at && <Button size="sm" disabled={busy} onClick={() => save({ ends_at: null }, CHANGE.done.endsClear(name))}>{CHANGE.endsClear}</Button>}
          <Button size="sm" onClick={onCancel} disabled={busy}>{PERSON.keep}</Button>
        </span>
      </div>
    )
  }
  if (kind === 'cohort') {
    const list = cohortsOf(cohorts, row.product_id)
    const picked = list.find(c => c.id === cohortId)
    const group = product?.type === 'mastermind'
    return (
      <div className="apl-change" role="group" aria-label={group ? CHANGE.group : CHANGE.cohort}>
        <div className="apl-change-fields">
          <label className="ad-field">
            <span className="ad-label ad-label--plain">{group ? CHANGE.groupLabel : CHANGE.cohortLabel}</span>
            <Select value={cohortId} onChange={setCohortId} placeholder={group ? CHANGE.noGroup : CHANGE.noCohort} options={list.map(c => ({ value: c.id, label: c.name }))} />
          </label>
        </div>
        {err}
        {actions(() => save({ cohort_id: cohortId || null }, (group ? CHANGE.done.group : CHANGE.done.cohort)(name, picked?.name)), group ? CHANGE.groupGo : CHANGE.cohortGo, (cohortId || null) === (row.cohort_id || null))}
      </div>
    )
  }
  if (kind === 'balance') {
    const at = row.balance_due_at ? hhmm(row.balance_due_at) : '18:00'
    return (
      <div className="apl-change" role="group" aria-label={CHANGE.balance}>
        <p className="apl-change-line">{CHANGE.balanceLine(first)}</p>
        <div className="apl-change-fields">
          <DateField label={CHANGE.balanceLabel} value={day} onChange={setDay} plain />
        </div>
        {err}
        {actions(() => save({ balance_due_at: ukToIso(day, at) }, CHANGE.done.balance(name, boardDay(day))), CHANGE.balanceGo, !day || day === ukDayOf(row.balance_due_at))}
      </div>
    )
  }
  return null
}

export function AccessCard({ data, amounts, role, first, extended, onGrace, onCard, onChanged, onGrantAgain, onEndSpace, onOpenSpace }) {
  const { api, products: adminProducts } = useAdmin()
  const oa = role === 'owner' || role === 'admin'
  const { person, access, billing, catalogue, cohorts, spaces, outside, hasCustomer } = data
  const productsById = new Map(catalogue.map(p => [p.id, p]))
  const cohortsById = new Map(cohorts.map(c => [c.id, c]))
  const billingBy = new Map(billing.map(b => [b.access_id, b]))
  const [open, setOpen] = useState(null) // { id, kind }
  // "Open a 1-2-1 space" while some live private client space product has no open space of theirs (the
  // product-types plan, 13 Oct: each product's spaces stay apart), with those that do, for the dialog
  const openProductIds = spaces.filter(s => !s.ended_at).map(s => s.product_id)
  const spaceToOpen = liveSpaceProducts(adminProducts).some(p => !openProductIds.includes(p.id))
  const [revoking, setRevoking] = useState(false)
  const [error, setError] = useState(null)
  // on a phone, the rows opened by a tap
  const phone = usePhone()
  const [shown, setShown] = useState(() => new Set())
  const toggle = id => setShown(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  // hub rows on products in the hub; the rest only as ownership outside it
  // current rows first, each group in the boards' product order
  const hubRows = access.filter(a => productsById.get(a.product_id)?.moved_in)
    .map(a => ({ ...a, slug: productsById.get(a.product_id)?.slug, sort_order: productsById.get(a.product_id)?.sort_order }))
  const ordered = [...byBoardOrder(hubRows.filter(isCurrent)), ...byBoardOrder(hubRows.filter(a => !isCurrent(a)))]
  const rows = [
    ...ordered.map(a => ({ kind: 'access', id: a.id, row: a, product: productsById.get(a.product_id) })),
    ...spaces.map(s => ({ kind: 'space', id: s.id, space: s, product: productsById.get(s.product_id) || { name: SPACE.name, slug: 'mentorship' } })),
    ...outside.map(o => ({ kind: 'outside', id: `outside-${o.product_id}`, outside: o, product: productsById.get(o.product_id) || { id: o.product_id } })),
  ]

  async function revoke(row) {
    setRevoking(true)
    setError(null)
    try {
      const answer = await api.revoke_access({ access_id: row.id, end_reason: 'revoked_by_staff' })
      setOpen(null)
      onChanged(answer.message)
    } catch (e) {
      setError(e)
    } finally {
      setRevoking(false)
    }
  }

  const columns = [
    {
      key: 'product', label: PERSON.heads(first).product, width: 'minmax(0, 1.25fr)', primary: true,
      render: r => {
        const text = (
          <span className="apl-product-text">
            <span className="apl-product-name">{r.kind === 'outside' ? `${r.product?.name || 'A product'}` : r.product?.name || SPACE.name}</span>
            <span className="apl-product-detail">
              {r.kind === 'access' && (standingDetail(standingFor(r.row, data.standing)) || accessDetail(r.row, r.product, cohortsById))}
              {r.kind === 'space' && SPACE.detail}
              {r.kind === 'outside' && `${shortName(r.product)}, outside the hub`}
            </span>
          </span>
        )
        return (
          <span className="apl-product">
            <Dot product={r.product} size={10} ringed />
            {/* on a phone the row is 1 line that opens on tap; a shut row's other cells are hidden (admin-people.css) */}
            {phone
              ? <button type="button" className="apl-row-toggle" aria-expanded={shown.has(r.id)} onClick={() => toggle(r.id)}>{text}<span className="apl-chev" aria-hidden="true" /></button>
              : text}
            {phone && !shown.has(r.id) && <span className="apl-row--shut" hidden />}
          </span>
        )
      },
    },
    {
      key: 'status', label: PERSON.heads(first).status, width: '150px',
      render: r => {
        if (r.kind === 'space') return <StatusPill status={r.space.ended_at ? 'ended' : 'active'}>{r.space.ended_at ? 'Ended' : 'Active'}</StatusPill>
        if (r.kind === 'outside') return <StatusPill tone="paused">{OUTSIDE_STATUS[r.outside.status] || 'Active'}</StatusPill>
        const text = accessStatus(r.row, { extendedTo: extended[r.row.id] })
        return <StatusPill tone={TONE[text]} status={r.row.status}>{text}</StatusPill>
      },
    },
    {
      key: 'how', label: PERSON.heads(first).how, width: 'minmax(0, 1.25fr)',
      render: r => {
        if (r.kind === 'space') return <span className="apl-cell-line">{SPACE.how(boardDay(r.space.opened_at))}</span>
        if (r.kind === 'outside') return <span className="apl-cell-line">{`On ${homeSite(r.product)} until it moves`}</span>
        return <span className="apl-cell-line">{howGot(r.row, { billing: billingBy.get(r.row.id), amounts, granter: data.granters?.get(r.row.id) })}</span>
      },
    },
    {
      key: 'runs', label: PERSON.heads(first).runs, width: 'minmax(0, 1fr)',
      render: r => {
        if (r.kind === 'space') return <span className="apl-cell-line">{r.space.ended_at ? SPACE.ended(boardDay(r.space.ended_at)) : SPACE.runs}</span>
        if (r.kind === 'outside') return <span className="apl-cell-line">{r.outside.checked_at ? `Checked ${boardDay(r.outside.checked_at)}` : ''}</span>
        return <span className="apl-cell-line">{accessRuns(r.row, { product: r.product, billing: billingBy.get(r.row.id) })}</span>
      },
    },
    {
      key: 'act', label: '', width: '168px', align: 'end',
      render: r => {
        if (r.kind === 'outside') return null
        if (r.kind === 'space') {
          return (
            <span className="apl-row-actions">
              {oa && <ButtonLink size="sm" to={spacePath(r.product, r.space.id)}>{PERSON.openSpace}</ButtonLink>}
              {role === 'owner' && !r.space.ended_at && (
                <Button size="sm" aria-expanded={open?.id === r.id} onClick={() => setOpen(o => (o?.id === r.id ? null : { id: r.id, kind: 'end' }))}>{PERSON.endSpace}</Button>
              )}
            </span>
          )
        }
        if (!oa) return null
        const name = r.product?.name || 'it'
        if (!isCurrent(r.row)) {
          return r.product && grantable(r.product)
            ? <Button size="sm" onClick={() => onGrantAgain(r.row.product_id)}>{PERSON.grantAgain}</Button>
            : null
        }
        const items = changeItems(r.row, { cohorts, billing: billingBy.get(r.row.id), hasCustomer, product: r.product })
        // on a phone, Revoke access is the Change menu's last item, so it's never the first button on a row
        if (phone) items.push(['revoke', PERSON.revokeItem])
        return (
          <span className="apl-row-actions">
            {!phone && (
              <Button size="sm" aria-expanded={open?.id === r.id && open.kind === 'revoke'}
                onClick={() => setOpen(o => (o?.id === r.id && o.kind === 'revoke' ? null : { id: r.id, kind: 'revoke' }))}>{PERSON.revoke}</Button>
            )}
            <ChangeMenu label={CHANGE.menu(name)} items={items} onPick={kind => {
              if (kind === 'grace') { onGrace(r.row); return }
              if (kind === 'card') { onCard(r.row); return }
              setOpen({ id: r.id, kind })
            }} />
          </span>
        )
      },
    },
  ]

  const after = r => {
    if (!open || open.id !== r.id) return null
    if (r.kind === 'space' && open.kind === 'end') {
      return (
        <ConfirmPanel text={SPACE.endConfirm(first)} confirmLabel={SPACE.endYes} cancelLabel={SPACE.endNo}
          onConfirm={async () => { const ok = await onEndSpace(r.space); if (ok) setOpen(null) }} onCancel={() => setOpen(null)} />
      )
    }
    if (r.kind !== 'access') return null
    const name = r.product?.name || 'it'
    if (open.kind === 'revoke') {
      const stripe = billingBy.get(r.row.id)?.stripe_subscription_id
      return (
        <ConfirmPanel text={PERSON.revokeConfirm(name, first) + (stripe ? PERSON.revokeStripe : '')} confirmLabel={PERSON.revokeYes}
          cancelLabel={PERSON.keep} busy={revoking} error={error} onConfirm={() => revoke(r.row)} onCancel={() => { setOpen(null); setError(null) }} />
      )
    }
    return (
      <ChangeForm kind={open.kind} row={r.row} product={r.product} cohorts={cohorts} first={first}
        onDone={message => { setOpen(null); onChanged(message) }} onCancel={() => setOpen(null)} />
    )
  }

  return (
    <Card title={PERSON.access} aside={PERSON.accessAside(first)} body={false} className="apl-access">
      {rows.length
        ? <Table flush label={PERSON.access} rowKey={r => r.id} rows={rows} columns={columns} after={after} />
        : <p className="ad-empty">{PERSON.noAccess(first)}</p>}
      {role === 'owner' && spaceToOpen && !person.role && (
        <div className="apl-access-foot">
          <Button size="sm" onClick={() => onOpenSpace(openProductIds)}>{PERSON.openNewSpace(first)}</Button>
        </div>
      )}
    </Card>
  )
}
